<template>
  <div id="app-sidebar" class="sidebar-menu list-group list-group-flush">
    <template v-for="(route, routeIndex) in filteredRoutes(routes)">
      <div class="mb-0 list-group-item list-group-item-group" :id="`route-${linkId(route.name)}`"
        :key="`route-group-${routeIndex}-${route.name}`" v-if="route.meta && route.meta.isGroup">
        <div class="text-nowrap text-uppercase d-flex align-items-center">
          <i class="menu-icon" :class="route.meta.icon || 'fa-solid fa-hashtag'" />
          <span class="menu-text ms-2">
            {{ route.meta.title || route.meta.name }}
          </span>
        </div>
        <router-link v-for="(child, childIndex) in filteredRoutes(route.children)"
          class="list-group-item list-group-item-action border-0" :key="`route-child=${childIndex}-${child.name}`"
          :to="child.path" data-bs-toggle="tooltip" data-bs-placement="top" :title="`${route.meta.title} - ${child.meta.title}` || child.meta.name
            ">
          <div class="text-nowrap d-flex align-items-center">
            <i class="menu-icon" :class="child.meta.icon || 'fa-solid fa-hashtag'" />
            <span class="menu-text ms-2">
              {{ child.meta.title || child.meta.name }}
            </span>
            <span class="badge rounded-pill ms-2 menu-text" :class="badgeColor(badge.name)"
              v-for="badge in visibleBadges(child.meta.badge)" :key="`counter-badge-${badge.name}`">
              <div class="spinner-border spinner-border-sm" role="status" v-if="isLoadingCounts"></div>
              <template v-else>{{ badgeValue(badge.name) }}</template>
            </span>
          </div>
        </router-link>
      </div>
      <router-link class="list-group-item list-group-item-action border-bottom" :id="`route-${linkId(route.name)}`"
        :key="`route-${routeIndex}-${route.name}`" :to="route.path" data-bs-toggle="tooltip" data-bs-placement="top"
        :title="route.meta.title || route.meta.name" v-else>
        <div class="text-nowrap">
          <i class="menu-icon" :class="route.meta.icon || 'fa-solid fa-hashtag'" />
          <span class="menu-text ms-2">
            {{ route.meta.title || route.meta.name }}
          </span>
        </div>
      </router-link>
    </template>
  </div>
</template>

<script>
import { Tooltip } from "bootstrap/dist/js/bootstrap.esm.min";

export default {
  props: ["selectedYear", "sidebarExpand"],
  data() {
    return {
      routes: [],
      user: null,
      isLoadingCounts: false,
      badges: {
        siteVisitCount: {
          new: { class: "bg-secondary", value: 0 },
          submitted: { class: "bg-warning text-dark blink", value: 0 },
        },
        bqCount: {
          kjp: { class: "bg-info text-dark blink", value: 0 },
          electrical: { class: "bg-info text-dark blink", value: 0 },
          emergency: { class: "bg-info text-dark blink", value: 0 },
        },
      },
    };
  },
  computed: {
    linkId() {
      return (path) => {
        return path.split(".")[0];
      };
    },
  },
  watch: {
    sidebarExpand: {
      handler() {
        this.toggleTooltipAvailability();
      },
      immediate: false,
    },
    "$route.name": {
      handler() {
        if (this.$route.name) {
          this.getNodCount();
        }
      },
      deep: true,
      immediate: false,
    },
    selectedYear: {
      handler() {
        this.getNodCount();
      },
      deep: true,
      immediate: false,
    },
  },
  methods: {
    resolveBadge(name) {
      return name.split(".").reduce((o, k) => (o == null ? undefined : o[k]), this.badges);
    },
    badgeValue(name) {
      const n = Number(this.resolveBadge(name)?.value);
      return Number.isFinite(n) ? n : 0;
    },
    badgeColor(name) {
      return this.resolveBadge(name)?.class || "bg-secondary";
    },
    visibleBadges(badges) {
      return (badges || []).filter(
        (b) =>
          b.showFor.includes(this.user?.role) &&
          (this.isLoadingCounts || this.badgeValue(b.name) > 0)
      );
    },
    getNodCount() {
      if (!this.Helper.jkrRoles.includes(this.user?.role)) return;

      this.isLoadingCounts = true;

      Object.keys(this.badges).forEach((key) => {
        Object.keys(this.badges[key]).forEach((child) => {
          this.badges[key][child]["value"] = 0;
        });
      });

      return new Promise(async (resolve, reject) => {
        let url = this.API.endpoints(this.selectedYear).allCases;

        if (this.user?.districts?.length) {
          this.user.districts.forEach((district) => {
            url += `&district_in=${district.id}`;
          });
        }

        const [call, error] = await this.Helper.handlePromise(
          this.API.get(url)
        );

        if (error) {
          console.error(error);

          reject(error);
        } else {
          call.data.forEach((nod) => {
            if (nod.componentStatus?.site_visit === "pending") {
              this.badges.siteVisitCount.submitted.value += 1;
            } else if (nod.componentStatus?.site_visit !== true) {
              this.badges.siteVisitCount.new.value += 1;
            } else if (
              nod.componentStatus?.site_visit === true &&
              nod.componentStatus?.bq &&
              (!nod.componentStatus?.memo || !nod.componentStatus?.claim) &&
              !nod.nod_bq?.isRead
            ) {
              if (nod.groupType?.toLowerCase() == "kjp") {
                this.badges.bqCount.kjp.value += 1;
              } else if (nod.groupType?.toLowerCase() == "electrical") {
                this.badges.bqCount.electrical.value += 1;
              } else if (nod.groupType?.toLowerCase() == "emergency") {
                this.badges.bqCount.emergency.value += 1;
              }
            }
          });

          resolve();
        }

        this.isLoadingCounts = false;
      });
    },
    filteredRoutes(routes) {
      return routes.filter(
        (r) =>
          !r.meta ||
          !r.meta.hideFor ||
          !r.meta.hideFor.includes(this.user && this.user.role)
      );
    },
    toggleTooltipAvailability() {
      if (this.sidebarExpand) {
        this.tooltipElements.map((elem) => {
          elem.disable();
        });
      } else {
        this.tooltipElements.map((elem) => {
          elem.enable();
        });
      }
    },
  },
  async mounted() {
    const _user = localStorage.getItem("user");

    if (_user) {
      this.user = JSON.parse(_user);
    } else {
      this.user = null;
    }

    await this.getNodCount();

    this.routes = this.$router.options.routes
      .filter((route) => route.meta && route.meta.includeInNavbar)
      .map((route) => {
        if (route.children) {
          route.children = route.children.filter(
            (child) => child.meta.includeInNavbar
          );
        }

        return route;
      });

    setTimeout(() => {
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
      );

      this.tooltipElements = tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new Tooltip(tooltipTriggerEl, {
          placement: "right",
        });
      });

      this.toggleTooltipAvailability();
    }, 500);
  },
};
</script>

<style></style>